import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FiPhone, FiMail, FiMapPin, FiZap, FiMessageCircle, FiSend, FiCheck } from 'react-icons/fi';
import { IMAGES } from '../constants/images';
import { CONTACT_EMAIL, getContactMailtoLink } from '../constants/contact';
import CTA from '../components/CTA';
import useIsMobile from '../utils/useIsMobile';

const DotGrid = () => (
  <div aria-hidden="true" style={{
    position: 'absolute', inset: 0, zIndex: 0,
    backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.12) 1px, transparent 1px)',
    backgroundSize: '28px 28px',
  }} />
);

const InfoCard = ({ icon: Icon, title, info, subInfo }) => (
  <div style={{
    background: '#fff', borderRadius: '18px', padding: '20px 16px',
    border: '1.5px solid #e8edf5', boxShadow: '0 2px 16px rgba(0,0,0,0.04)',
    textAlign: 'center', transition: 'transform 0.22s, box-shadow 0.22s', cursor: 'default',
  }}
    onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 12px 32px rgba(37,99,235,0.12)'; }}
    onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 16px rgba(0,0,0,0.04)'; }}
  >
    <div style={{ width: 46, height: 46, borderRadius: '13px', background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
      <Icon style={{ width: 20, height: 20, color: '#2563eb' }} />
    </div>
    <p style={{ fontWeight: 700, color: '#0f172a', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.07em', margin: '0 0 4px' }}>{title}</p>
    <p style={{ fontWeight: 800, color: '#1e3a8a', fontSize: '0.82rem', margin: '0 0 3px', wordBreak: 'break-all', overflowWrap: 'anywhere' }}>{info}</p>
    <p style={{ color: '#94a3b8', fontSize: '0.7rem', margin: 0, lineHeight: 1.5 }}>{subInfo}</p>
  </div>
);

const Field = ({ label, required, children }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
    <label style={{ fontWeight: 800, fontSize: '0.82rem', color: '#0f172a', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
      {label}{required && <span style={{ color: '#ef4444', marginLeft: 3 }}>*</span>}
    </label>
    {children}
  </div>
);

const inputStyle = {
  width: '100%', boxSizing: 'border-box',
  padding: '13px 16px', borderRadius: '12px',
  border: '1.5px solid #e2e8f0', background: '#f8fafc',
  fontSize: '1rem', color: '#0f172a', outline: 'none',
  transition: 'border-color 0.2s, box-shadow 0.2s', fontFamily: 'inherit',
};

const focusStyle = (e) => { e.target.style.borderColor = '#3b82f6'; e.target.style.boxShadow = '0 0 0 4px rgba(59,130,246,0.1)'; e.target.style.background = '#fff'; };
const blurStyle = (e) => { e.target.style.borderColor = '#e2e8f0'; e.target.style.boxShadow = 'none'; e.target.style.background = '#f8fafc'; };

const ContactPage = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', address: '', service: '', message: '' });
  const [consent, setConsent] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState('');
  const isMobile = useIsMobile(900);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!consent) { alert('Please agree to the terms before submitting.'); return; }
    setLoading(true);
    setFormError('');
    try {
      const payload = {
        access_key: '3949992a-2870-41b1-b8d4-48584c8714dd',
        subject: `Contact form: ${formData.service || 'Inquiry'} from ${formData.name || 'Visitor'}`,
        ...formData,
      };
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success !== false) {
        setSubmitted(true);
      } else {
        setFormError('Something went wrong. Use the button below to send your message by email instead.');
      }
    } catch {
      setFormError('Connection issue. Use the button below to send your message by email instead.');
    } finally {
      setLoading(false);
    }
  };

  const contactInfo = [
    { icon: FiPhone, title: 'Phone', info: '+1 (888) 880-2585', subInfo: 'Mon–Fri: 9AM–6PM, Sat: 9AM–5PM, Sun: closed' },
    { icon: FiMail, title: 'Email', info: 'contact@internettvservice.com', subInfo: 'We aim to reply within one business day' },
    { icon: FiMapPin, title: 'Office', info: '13800 Coppermine Rd', subInfo: 'Herndon, VA 20171' },
    { icon: FiZap, title: 'Response Time', info: 'Typically 1 business day', subInfo: 'We do our best to respond quickly' },
  ];

  return (
    <main style={{ fontFamily: "'DM Sans', 'Segoe UI', sans-serif", background: '#f8f9fc' }}>

      {/* ── HERO ─────────────────────────────────────────────────────── */}
      <section style={{ position: 'relative', minHeight: isMobile ? 'clamp(320px, 70vh, 400px)' : 'clamp(440px, 50vh, 560px)', display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
        <img src={IMAGES.heroContact} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} onError={e => { e.currentTarget.src = IMAGES.hero?.[2] ?? ''; }} />
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(120deg, rgba(10,20,60,0.85) 0%, rgba(10,20,60,0.6) 55%, rgba(10,20,60,0.25) 100%)' }} />
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(10,20,60,0.5) 0%, transparent 40%)' }} />
        <DotGrid />

        <div style={{ position: 'relative', zIndex: 1, width: '100%', padding: isMobile ? 'clamp(48px, 10vw, 64px) var(--cc-px) clamp(36px, 8vw, 48px)' : 'clamp(72px, 8vw, 100px) var(--cc-px) 70px', textAlign: 'center' }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255,255,255,0.2)', borderRadius: 999,
            padding: '6px 18px', marginBottom: 22,
          }}>
            <FiMessageCircle style={{ color: '#60a5fa', width: 15, height: 15 }} />
            <span style={{ color: 'rgba(255,255,255,0.95)', fontSize: '0.82rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Get In Touch</span>
          </div>
          <h1 style={{ color: '#fff', fontSize: 'clamp(2rem, 5vw, 6rem)', fontWeight: 900, lineHeight: 1.1, margin: '0 0 16px', letterSpacing: '-0.02em' }}>
            We're Here to <span style={{ color: '#60a5fa' }}>Help You</span>
          </h1>
          <p style={{ color: 'rgba(255,255,255,0.88)', fontSize: isMobile ? '0.97rem' : '1.2rem', maxWidth: 640, margin: '0 auto', lineHeight: 1.85 }}>
            Have a question or ready to get connected? Our team does its best to respond promptly and help you find the right solution.
          </p>
        </div>
      </section>

      {/* ── INFO CARDS ───────────────────────────────────────────────── */}
      <section style={{ padding: isMobile ? 'clamp(24px, 5vw, 36px) var(--cc-px) 0' : 'clamp(48px, 5vw, 72px) var(--cc-px) 0' }}>
        <div className="cc-container" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(140px, 100%), 1fr))', gap: 12 }}>
          {contactInfo.map((item, i) => <InfoCard key={i} {...item} />)}
        </div>
      </section>

      {/* ── FORM SECTION ─────────────────────────────────────────────── */}
      <section style={{ padding: isMobile ? 'clamp(24px, 5vw, 32px) var(--cc-px) clamp(40px, 8vw, 64px)' : 'clamp(48px, 5vw, 72px) var(--cc-px) 80px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(300px, 100%), 1fr))', gap: isMobile ? 24 : 40, alignItems: 'start', maxWidth: 'min(100%, var(--content-max))', margin: '0 auto' }}>

          {/* LEFT — copy / trust signals */}
          <div style={{ paddingTop: isMobile ? 0 : 8 }}>
            <h2 style={{ fontSize: isMobile ? '1.5rem' : '2rem', fontWeight: 900, color: '#0f172a', margin: '0 0 12px', lineHeight: 1.15, letterSpacing: '-0.02em' }}>
              Send Us a Message
            </h2>
            <p style={{ color: '#64748b', lineHeight: 1.75, margin: '0 0 24px', fontSize: '0.95rem' }}>
              Fill out the form and our team will get back to you as soon as we can. No spam, ever.
            </p>

            {[
              'We aim to respond within one business day',
              'Straightforward information about plans and pricing',
              'Sales & enrollment help from our team',
              'Free coverage check included',
            ].map((t, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 13 }}>
                <span style={{ width: 22, height: 22, borderRadius: '50%', background: '#dbeafe', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <FiCheck style={{ width: 12, height: 12, color: '#2563eb' }} />
                </span>
                <span style={{ color: '#1f2937', fontSize: '0.95rem', fontWeight: 600 }}>{t}</span>
              </div>
            ))}

            {/* Map embed */}
            <div style={{ marginTop: 24, borderRadius: 16, overflow: 'hidden', height: isMobile ? 180 : 220, border: '1.5px solid #e2e8f0', boxShadow: '0 4px 20px rgba(0,0,0,0.06)' }}>
              <iframe
                title="Office Location"
                src="https://www.google.com/maps?q=13800+Coppermine+Rd,+Herndon,+VA+20171&output=embed"
                style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          {/* RIGHT — Form card */}
          <div style={{ background: '#fff', borderRadius: 22, overflow: 'hidden', boxShadow: '0 16px 60px rgba(0,0,0,0.08)', border: '1.5px solid #e8edf5' }}>
            {/* Card header */}
            <div style={{
              background: 'linear-gradient(135deg, #0f172a 0%, #1e3a8a 100%)',
              padding: isMobile ? '22px 20px' : '28px 32px',
              position: 'relative', overflow: 'hidden',
            }}>
              <div aria-hidden style={{ position: 'absolute', top: -40, right: -40, width: 160, height: 160, borderRadius: '50%', border: '36px solid rgba(255,255,255,0.05)' }} />
              <h3 style={{ color: '#fff', fontSize: '1.15rem', fontWeight: 800, margin: '0 0 4px' }}>Contact Form</h3>
              <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.82rem', margin: 0 }}>All fields marked * are required</p>
            </div>

            {/* Form body */}
            {submitted ? (
              <div style={{ padding: isMobile ? '44px 20px' : '64px 36px', textAlign: 'center' }}>
                <div style={{ width: 60, height: 60, borderRadius: '50%', background: '#d1fae5', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                  <FiCheck style={{ width: 26, height: 26, color: '#059669' }} />
                </div>
                <h3 style={{ fontSize: '1.3rem', fontWeight: 800, color: '#0f172a', margin: '0 0 10px' }}>Message Sent!</h3>
                <p style={{ color: '#64748b', lineHeight: 1.7, margin: '0 0 20px' }}>
                  Thanks for reaching out. Our team will contact you as soon as possible at the email you provided.
                </p>
                <p style={{ color: '#64748b', fontSize: '0.9rem', margin: 0 }}>
                  Prefer to email us directly?{' '}
                  <a
                    href={`mailto:${CONTACT_EMAIL}?subject=Contact%20form%20inquiry`}
                    style={{ color: '#2563eb', fontWeight: 700, textDecoration: 'none' }}
                    onMouseEnter={e => { e.currentTarget.style.textDecoration = 'underline'; }}
                    onMouseLeave={e => { e.currentTarget.style.textDecoration = 'none'; }}
                  >
                    Send an email
                  </a>
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} style={{ padding: isMobile ? '24px 20px' : '32px 32px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? 16 : 20 }}>

                  <Field label="Full Name" required>
                    <input type="text" name="name" value={formData.name} onChange={handleChange} placeholder="John Doe" required style={inputStyle} onFocus={focusStyle} onBlur={blurStyle} />
                  </Field>

                  <Field label="Email Address" required>
                    <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="john@example.com" required style={inputStyle} onFocus={focusStyle} onBlur={blurStyle} />
                  </Field>

                  <Field label="Phone Number">
                    <input type="tel" name="phone" value={formData.phone} onChange={handleChange} placeholder="(555) 555-5555" style={inputStyle} onFocus={focusStyle} onBlur={blurStyle} />
                  </Field>

                  <Field label="Service Interest">
                    <select name="service" value={formData.service} onChange={handleChange}
                      style={{ ...inputStyle, color: formData.service ? '#0f172a' : '#94a3b8' }}
                      onFocus={focusStyle} onBlur={blurStyle}>
                      <option value="" disabled>Select a service</option>

                      <option value="internet">Internet</option>
                      <option value="tv">TV</option>

                    </select>
                  </Field>

                  <div style={{ gridColumn: '1 / -1' }}>
                    <Field label="Installation Address">
                      <input type="text" name="address" value={formData.address} onChange={handleChange} placeholder="Enter your full address" style={inputStyle} onFocus={focusStyle} onBlur={blurStyle} />
                    </Field>
                  </div>

                  <div style={{ gridColumn: '1 / -1' }}>
                    <Field label="Message">
                      <textarea name="message" value={formData.message} onChange={handleChange} placeholder="Tell us about your needs..." rows={4} style={{ ...inputStyle, resize: 'none', lineHeight: 1.6 }} onFocus={focusStyle} onBlur={blurStyle} />
                    </Field>
                  </div>

                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer' }}>
                      <input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)} required style={{ width: 17, height: 17, accentColor: '#2563eb', cursor: 'pointer', flexShrink: 0, marginTop: 2 }} />
                      <span style={{ color: '#64748b', fontSize: '0.75rem', lineHeight: 1.65 }}>
                        By submitting, I agree that <strong style={{ color: '#334155' }}>InternetTV Service</strong> and its authorized partners may contact me at the number and email I provide about the services I requested — including by automated dialing technology and prerecorded messages. Consent is <strong style={{ color: '#334155' }}>not a condition of purchase</strong>. I confirm I own this phone number. My details are used to respond to my request and recommend plans, as described in our{' '}
                        <Link to="/privacy" style={{ color: '#2563eb', fontWeight: 700 }}>Privacy Policy</Link> and{' '}
                        <Link to="/terms" style={{ color: '#2563eb', fontWeight: 700 }}>Terms &amp; Conditions</Link>. Message &amp; data rates may apply; reply "STOP" to opt out.
                      </span>
                    </label>
                  </div>

                  {formError && (
                    <div style={{ gridColumn: '1 / -1' }}>
                      <div style={{ color: '#ef4444', fontSize: '0.85rem', padding: '10px 14px', background: '#fef2f2', borderRadius: 10, border: '1px solid #fecaca', marginBottom: 12 }}>
                        {formError}
                      </div>
                      <a
                        href={getContactMailtoLink(formData)}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 8,
                          gridColumn: '1 / -1',
                          padding: '12px 20px', borderRadius: 10, border: '2px solid #2563eb',
                          background: '#eff6ff', color: '#1d4ed8', fontWeight: 700, fontSize: '0.95rem',
                          textDecoration: 'none', marginBottom: 8,
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = '#dbeafe'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = '#eff6ff'; }}
                      >
                        <FiMail style={{ width: 18, height: 18 }} />
                        Send my message via email instead
                      </a>
                    </div>
                  )}
                  <div style={{ gridColumn: '1 / -1', marginTop: 4 }}>
                    <button type="submit" disabled={loading} style={{
                      width: '100%', background: 'linear-gradient(135deg, #1d4ed8 0%, #2563eb 100%)',
                      color: '#fff', fontWeight: 700, fontSize: '1rem',
                      padding: '15px 24px', borderRadius: '13px', border: 'none', cursor: loading ? 'not-allowed' : 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                      boxShadow: '0 4px 20px rgba(37,99,235,0.35)', transition: 'opacity 0.2s, transform 0.15s',
                      fontFamily: 'inherit', opacity: loading ? 0.7 : 1,
                    }}
                      onMouseEnter={e => { if (!loading) { e.currentTarget.style.opacity = '0.92'; e.currentTarget.style.transform = 'translateY(-1px)'; } }}
                      onMouseLeave={e => { e.currentTarget.style.opacity = loading ? '0.7' : '1'; e.currentTarget.style.transform = 'translateY(0)'; }}
                    >
                      <FiSend style={{ width: 17, height: 17 }} />
                      {loading ? 'Sending…' : 'Send Message'}
                    </button>
                    <p style={{ color: '#94a3b8', fontSize: '0.72rem', lineHeight: 1.6, margin: '10px 0 0', textAlign: 'center' }}>
                      By submitting, you consent to be contacted by phone, text, or email (including by autodialer/prerecorded messages) about your request. Consent is not a condition of purchase. Msg &amp; data rates may apply; reply "STOP" to opt out.
                    </p>
                  </div>

                </div>
              </form>
            )}
          </div>

        </div>
      </section>

      <CTA />
    </main>
  );
};

export default ContactPage;
